import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button';
import {
  RotateCcw,
  AlertTriangle
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';
import { globalNavigator } from '../WindowContent';
import { CONTROL_PANEL_ITEMS } from './controlPanelIcons';

interface ControlPanelProps {
  onOpenWindow?: (appId: string, title: string) => void;
  onClose?: () => void;
  onShowSplash?: (show: boolean) => void;
}

export const ControlPanel: React.FC<ControlPanelProps> = ({ onOpenWindow, onClose, onShowSplash }) => {
  const [isResetting, setIsResetting] = useState(false);
  const [isCompactLayout, setIsCompactLayout] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) {
      return;
    }

    const updateLayout = () => {
      setIsCompactLayout(element.clientWidth < 1120);
    };

    updateLayout();

    const observer = new ResizeObserver(() => {
      updateLayout();
    });

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, []);

  const handleResetMapleOS = async () => {
    console.log('Reset MapleOS button clicked - starting reset process');
    console.log('Setting isResetting to true, showSplash to true');
    setIsResetting(true);
    
    if (onShowSplash) {
      onShowSplash(true);
    }
    
    if (onClose) {
      setTimeout(() => onClose(), 300);
    }
    
    console.log('Splash should now be visible');
    
    try {
      console.log('Calling globalNavigator.resetFileSystem()');
      
      const resetPromise = globalNavigator.resetFileSystem();
      await resetPromise;
      
      console.log('Reset completed, dispatching filesystem change event');
      
      window.dispatchEvent(new CustomEvent('fileSystemChanged', {
        detail: { path: 'C:\\' }
      }));
      
      console.log('Reset successful');
    } catch (error) {
      console.error('Failed to reset MapleOS:', error);
      if (onShowSplash) {
        onShowSplash(false);
      }
      setIsResetting(false);
      alert('Failed to reset MapleOS. Please try again.');
    }
  };

  const handleSplashComplete = () => {
    console.log('Splash completion called');
    setIsResetting(false);
    setTimeout(() => {
      alert('MapleOS has been reset successfully! All desktop items and files have been restored to default.');
    }, 300);
  };

  return (
    <div ref={containerRef} className="h-full overflow-auto bg-background p-6">
      <div className="mb-8">
        <h1 className="mb-2 text-4xl font-bold tracking-tight text-foreground">Control Panel</h1>
        <p className="text-xl text-muted-foreground">
          Change settings for your computer's hardware and software
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {CONTROL_PANEL_ITEMS.map((item) => {
          const Icon = item.Icon;

          return (
            <Button
              key={item.id}
              variant="ghost"
              className={`group h-auto flex-col items-center rounded-2xl border border-border/80 bg-card/70 p-5 text-center shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-card hover:shadow-md ${isCompactLayout ? 'min-h-[142px]' : 'min-h-[184px]'}`}
              onClick={() => {
                if (onOpenWindow) {
                  onOpenWindow(item.appId, item.windowTitle);
                }
              }}
            >
              <Icon size={48} className={`h-12 w-12 object-contain drop-shadow-sm transition-transform duration-200 group-hover:scale-105 ${isCompactLayout ? 'mb-3' : 'mb-4'}`} />
              <div className={isCompactLayout ? 'space-y-0' : 'space-y-1.5'}>
                <div className="text-xl font-semibold tracking-tight text-foreground">{item.name}</div>
                {!isCompactLayout && (
                  <div className="text-sm leading-5 text-muted-foreground">
                    {item.description}
                  </div>
                )}
              </div>
            </Button>
          );
        })}
      </div>

      <div className="mt-8 border-t border-border pt-6">
        <h2 className="mb-4 text-lg font-semibold">Quick Actions</h2>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" size="sm">
            System Information
          </Button>
          <Button variant="secondary" size="sm">
            Device Manager
          </Button>
          <Button variant="secondary" size="sm">
            Add/Remove Programs
          </Button>
          <Button variant="secondary" size="sm">
            Regional Settings
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              window.dispatchEvent(new CustomEvent('mapleos-apply-template', {
                detail: { name: 'bug-repro', vars: {} }
              }));
            }}
          >
            Create Bug Repro Bundle
          </Button>
        </div>
      </div>

      <div className="mt-8 border-t border-destructive/20 pt-6">
        <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-destructive">
          <AlertTriangle size={20} />
          Danger Zone
        </h2>
        <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <h3 className="font-medium text-destructive">Reset MapleOS</h3>
              <p className="max-w-md text-sm text-muted-foreground">
                This will permanently delete all your files, desktop items, and settings.
                The system will be restored to its original state.
              </p>
            </div>
            
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="destructive" 
                  size="sm" 
                  className="ml-4 flex items-center gap-2"
                  disabled={isResetting}
                >
                  <RotateCcw size={16} />
                  {isResetting ? 'Resetting...' : 'Reset MapleOS'}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2 text-destructive">
                    <AlertTriangle size={20} />
                    Reset MapleOS?
                  </AlertDialogTitle>
                  <AlertDialogDescription asChild>
                    <div className="space-y-3">
                      <div className="font-medium text-foreground">
                        This action cannot be undone!
                      </div>
                      <div>
                        Resetting MapleOS will:
                      </div>
                      <ul className="ml-4 list-inside list-disc space-y-1 text-sm">
                        <li>Delete all files and folders you've created</li>
                        <li>Remove all desktop items</li>
                        <li>Restore default system folders (MapleOS, Program Files, etc.)</li>
                        <li>Reset all applications to their default state</li>
                      </ul>
                      <div className="font-medium text-destructive">
                        Are you absolutely sure you want to continue?
                      </div>
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={isResetting}>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleResetMapleOS}
                    disabled={isResetting}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {isResetting ? 'Resetting...' : 'Yes, Reset MapleOS'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>
    </div>
  );
};